<template>
  <div
    class="blog-author"
    :class="{ featured, 'in-content': inContent, 'is-link': isLink }"
    @click="isLink ? push(to) : undefined"
  >
    <UiComponentImage
      :alt="author.name"
      class="avatar"
      :expected-size="imageSize"
      :height="imageSize"
      :source="author.avatar.url"
      :width="imageSize"
    />
    <div class="info">
      <UiComponentParagraph
        v-if="inContent"
        class="about"
      >
        <slot />
      </UiComponentParagraph>
      <NuxtLink
        v-if="isLink"
        :to="to"
      >
        <UiComponentParagraph
          v-if="inContent"
          class="name"
        >
          {{ author.name }} - {{ author.job }}
        </UiComponentParagraph>
        <UiComponentHeading
          v-else
          :level="featured ? 3 : 2"
          class="name"
        >
          {{
            author.name
          }}
        </UiComponentHeading>
      </NuxtLink>
      <div v-else>
        <UiComponentParagraph
          v-if="inContent"
          class="name"
        >
          {{ author.name }} - {{ author.job }}
        </UiComponentParagraph>
        <UiComponentHeading
          v-else
          :level="featured ? 3 : 2"
          class="name"
        >
          {{
            author.name
          }}
        </UiComponentHeading>
      </div>
      <UiComponentParagraph
        v-if="!inContent"
        class="job"
      >
        {{
          author.job
        }}
      </UiComponentParagraph>
      <UiComponentParagraph
        v-if="!featured"
        class="text"
      >
        {{
          author.text
        }}
      </UiComponentParagraph>
      <div
        v-if="!featured && author.socials && author.socials.length > 0"
        class="socials"
      >
        <AtomButton
          v-for="{ id, icon, text } in author.socials"
          :key="id"
          type="text"
          :to="text"
        >
          <UiComponentImage
            :alt="icon.alt"
            :expected-size="16"
            :height="16"
            :source="icon.url"
            :width="16"
          />
        </AtomButton>
      </div>
    </div>
    <UiComponentIcon
      v-if="featured"
      type="arrow-right"
    />
  </div>
</template>

<script setup>
// Composables
const localePath = useLocalePath()
const { push } = useRouter()
const { width: screenWidth } = useScreenSize()

// Props
const props = defineProps({
  author: {
    required: true,
    type: Object,
  },
  featured: {
    default() {
      return false
    },
    required: false,
    type: Boolean,
  },
  inContent: {
    default() {
      return false
    },
    required: false,
    type: Boolean,
  },
  isLink: {
    default() {
      return true
    },
    required: false,
    type: Boolean,
  },
  socials: {
    default() {
      return []
    },
    required: false,
    type: Array,
  },
})

// Computed Properties
const imageSize = computed(() => {
  if (props.featured) {
    return 64
  }
  else {
    return screenWidth.value <= 768 ? 32 : 80
  }
})

const to = computed(() => {
  return localePath({
    name: 'blog-author-slug',
    params: {
      slug: props.author.slug,
    },
  })
})
</script>

<style lang="postcss" scoped>
.blog-author {
  @apply flex flex-row w-full p-6 bg-white rounded-4xl shadow-light2;

  @screen md {
    @apply p-10 items-center;
  }

  &.in-content {
    @apply px-0 shadow-none;

    &:hover {
      .name {
        @apply text-blizzard-700;
      }
    }

    .about {
      @apply text-sm leading-6 font-medium text-grey-blue-900;
    }

    .name {
      @apply mt-1 text-lg leading-7 transition-colors duration-300 ease-in-out !important;
    }

    .text {
      @apply mt-2;
    }
  }

  &.featured {
    @apply p-8 w-full items-center bg-blizzard-700 shadow-dark2 rounded-4xl transition-shadow duration-300 ease-in-out;

    &:hover {
      @apply shadow-dark4;

      :deep(i.icon-arrow-right) {
        transform: translateX(8px);
      }
    }

    .avatar {
      @apply rounded-full;
    }

    .info {
      .name {
        @apply font-medium text-white text-xl leading-7;
      }

      .job {
        @apply mt-0 text-white text-sm leading-6;
      }
    }

    i {
      @apply ml-auto text-white transition duration-300 ease-out;
    }
  }

  &.is-link {
    @apply cursor-pointer;
  }

  .avatar {
    @apply flex-shrink-0 h-8 w-8 rounded-full overflow-hidden;

    @screen md {
      @apply h-20 w-20;
    }
  }

  .info {
    @apply flex flex-col justify-start ml-4;

    @screen md {
      @apply ml-8;
    }

    .name {
      @apply font-medium text-2xl leading-8 text-winter-green-900;
    }

    .job {
      @apply mt-1 text-sm leading-6 text-grey-blue-600;
    }

    .text {
      @apply mt-2 text-sm leading-6 text-grey-blue-600;
    }

    .socials {
      @apply mt-2 flex;

      .button {
        @apply ml-6 px-0 h-4;

        &:first-child {
          @apply ml-0;
        }

        picture {
          @apply rounded-none;
        }
      }
    }
  }
}
</style>
